/**
 * Trait definitions: what each score, measurement and EPD means here, with units, scales, codes and
 * association names. Changing a meaning publishes a new version; older records keep the old one.
 */
import { useState } from "react";
import { ApiError, api } from "../../../web/lib/api";
import { t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { Button, Chip, ErrorNote, Field, IconButton, List, Loading, Row, Section, Select, Sheet, TextArea, TextInput, toast } from "../../../web/ui/kit";
import { TRAIT_DIRECTION_LABELS, TRAIT_KIND_LABELS, TRAIT_METHOD_LABELS, type TraitDefinition, type TraitKind } from "../shared/traits";
import type { TraitsDTO } from "../shared/types";
import { GeneticsShell } from "./GeneticsWorkspace";
import { traitName, useTraits } from "./ui";

type Trait = TraitsDTO["traits"][number];

function scaleText(x: TraitDefinition): string {
  if (x.scale) return t("{{min}} to {{max}}", { min: x.scale.min, max: x.scale.max });
  if (x.codes?.length) return t("{{count}} codes", { count: x.codes.length });
  return x.unit ?? "";
}

function Detail({ trait, onEdit }: { trait: Trait; onEdit: () => void }) {
  const can = useCan();
  return (
    <div className="stack">
      <dl className="data-list">
        <div><dt>{t("Key")}</dt><dd className="mono">{trait.key}</dd></div>
        <div><dt>{t("Kind")}</dt><dd>{t(TRAIT_KIND_LABELS[trait.kind])} · {t(TRAIT_METHOD_LABELS[trait.method])}</dd></div>
        {trait.unit && <div><dt>{t("Unit")}</dt><dd>{trait.unit}</dd></div>}
        {trait.scale && <div><dt>{t("Score scale")}</dt><dd>{scaleText(trait)}{trait.scale.step ? ` · ${t("steps of {{step}}", { step: trait.scale.step })}` : ""}</dd></div>}
        {trait.direction && <div><dt>{t("Better")}</dt><dd>{t(TRAIT_DIRECTION_LABELS[trait.direction])}</dd></div>}
        {trait.description && <div><dt>{t("About")}</dt><dd>{trait.custom ? trait.description : t(trait.description)}</dd></div>}
        <div><dt>{t("Version")}</dt><dd>{trait.version}{trait.versions > 1 ? ` · ${t("{{count}} versions kept", { count: trait.versions })}` : ""}</dd></div>
      </dl>
      {trait.codes?.length ? (
        <Section title={t("Codes")}>
          <List>{trait.codes.map((c) => <Row key={c.code} leading={<span className="genetics-qty" style={{ fontSize: 18, minWidth: 28 }}>{c.code}</span>} title={trait.custom ? c.label : t(c.label)} />)}</List>
        </Section>
      ) : null}
      {trait.mappings?.length ? (
        <Section title={t("What associations call it")}>
          <div className="genetics-targets">{trait.mappings.map((m) => <Chip key={`${m.association}${m.code}`} tone="neutral">{m.association}: <span className="mono">{m.code}</span></Chip>)}</div>
        </Section>
      ) : null}
      {can("manage") && <div className="form-actions"><Button icon="edit" onClick={onEdit}>{t("Publish a new version")}</Button></div>}
    </div>
  );
}

const blank: Omit<TraitDefinition, "key" | "version"> = { label: "", kind: "score", method: "observed" };

function TraitForm({ existing, onDone }: { existing?: Trait; onDone: () => void }) {
  const [key, setKey] = useState(existing?.key ?? "");
  const [v, setV] = useState<Omit<TraitDefinition, "key" | "version">>(existing ? { ...existing } : blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const set = (patch: Partial<TraitDefinition>) => setV({ ...v, ...patch });
  const num = (s: string) => (s.trim() === "" || Number.isNaN(Number(s)) ? undefined : Number(s));
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        label: existing && !existing.custom ? existing.label : v.label, label_es: v.label_es || undefined, description: v.description || undefined, kind: v.kind, method: v.method, unit: v.unit || undefined,
        scale: v.scale && v.scale.min !== undefined && v.scale.max !== undefined ? v.scale : undefined, codes: v.codes?.filter((c) => c.code && c.label), direction: v.direction,
        stage: v.stage || undefined, mappings: v.mappings?.filter((m) => m.association && m.code), note: v.note || undefined,
      };
      if (existing) await api.put(`/m/genetics/traits/${encodeURIComponent(existing.key)}`, body);
      else await api.post("/m/genetics/traits", { ...body, key });
      await invalidateAll();
      toast(t("Saved"), { tone: "good" });
      onDone();
    } catch (err) { setError(err instanceof ApiError ? err : new Error(t("Something went wrong on our side. Try again."))); } finally { setBusy(false); }
  };
  const codes = v.codes ?? [];
  const maps = v.mappings ?? [];
  return (
    <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      {!existing && <Field label={t("Key")} hint={t("A short id that never changes, like foot_score.")}><TextInput className="mono" value={key} onChange={(e) => setKey(e.target.value.replace(/\s+/g, "_"))} /></Field>}
      {(!existing || existing.custom) && <Field label={t("Name in English")}><TextInput value={v.label} onChange={(e) => set({ label: e.target.value })} /></Field>}
      {(!existing || existing.custom) && <Field label={t("Name in Spanish")} optional><TextInput value={v.label_es ?? ""} onChange={(e) => set({ label_es: e.target.value })} /></Field>}
      <Field label={t("Kind")}>
        <Select value={v.kind} onChange={(e) => set({ kind: e.target.value as TraitKind })}>{Object.entries(TRAIT_KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}</Select>
      </Field>
      <Field label={t("How it's known")}>
        <Select value={v.method} onChange={(e) => set({ method: e.target.value as TraitDefinition["method"] })}>{Object.entries(TRAIT_METHOD_LABELS).map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}</Select>
      </Field>
      <Field label={t("Unit")} optional><TextInput value={v.unit ?? ""} onChange={(e) => set({ unit: e.target.value })} placeholder={t("lb, in, cm, %")} /></Field>
      {(v.kind === "score" || v.kind === "measure") && (
        <Field label={t("Score scale")} optional>
          <div className="genetics-target-row" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            <TextInput aria-label={t("Lowest")} placeholder={t("Lowest")} inputMode="decimal" value={v.scale?.min ?? ""} onChange={(e) => set({ scale: { ...(v.scale ?? { max: 0 }), min: num(e.target.value) as number } })} />
            <TextInput aria-label={t("Highest")} placeholder={t("Highest")} inputMode="decimal" value={v.scale?.max ?? ""} onChange={(e) => set({ scale: { ...(v.scale ?? { min: 0 }), max: num(e.target.value) as number } })} />
            <TextInput aria-label={t("Step")} placeholder={t("Step")} inputMode="decimal" value={v.scale?.step ?? ""} onChange={(e) => set({ scale: { ...(v.scale ?? { min: 0, max: 0 }), step: num(e.target.value) } })} />
          </div>
        </Field>
      )}
      {(v.kind === "category" || v.kind === "score") && (
        <Field label={t("Codes")} optional={v.kind !== "category"}>
          <div className="stack" style={{ gap: 6 }}>
            {codes.map((c, i) => (
              <div key={i} className="genetics-target-row" style={{ gridTemplateColumns: "64px 1fr 44px" }}>
                <TextInput aria-label={t("Code")} className="mono" value={c.code} onChange={(e) => set({ codes: codes.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)) })} />
                <TextInput aria-label={t("Meaning")} value={c.label} onChange={(e) => set({ codes: codes.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                <IconButton icon="delete" label={t("Remove")} onClick={() => set({ codes: codes.filter((_, j) => j !== i) })} />
              </div>
            ))}
            <div><Button size="sm" icon="plus" onClick={() => set({ codes: [...codes, { code: String(codes.length + 1), label: "" }] })}>{t("Add a code")}</Button></div>
          </div>
        </Field>
      )}
      <Field label={t("Better")} optional>
        <Select value={v.direction ?? ""} onChange={(e) => set({ direction: (e.target.value || undefined) as TraitDefinition["direction"] })}>
          <option value="">{t("Choose…")}</option>{Object.entries(TRAIT_DIRECTION_LABELS).map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
        </Select>
      </Field>
      <Field label={t("What associations call it")} optional>
        <div className="stack" style={{ gap: 6 }}>
          {maps.map((m, i) => (
            <div key={i} className="genetics-target-row" style={{ gridTemplateColumns: "1fr 1fr 44px" }}>
              <TextInput aria-label={t("Association")} value={m.association} onChange={(e) => set({ mappings: maps.map((x, j) => (j === i ? { ...x, association: e.target.value } : x)) })} placeholder={"RAAA"} />
              <TextInput aria-label={t("Their code")} className="mono" value={m.code} onChange={(e) => set({ mappings: maps.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)) })} />
              <IconButton icon="delete" label={t("Remove")} onClick={() => set({ mappings: maps.filter((_, j) => j !== i) })} />
            </div>
          ))}
          <div><Button size="sm" icon="plus" onClick={() => set({ mappings: [...maps, { association: "", code: "" }] })}>{t("Add a name")}</Button></div>
        </div>
      </Field>
      <Field label={t("About")} optional><TextArea value={existing && !existing.custom && v.description === existing.description ? t(v.description ?? "") : v.description ?? ""} rows={2} onChange={(e) => set({ description: e.target.value })} /></Field>
      {existing && <Field label={t("Why the new version?")} optional><TextInput value={v.note ?? ""} onChange={(e) => set({ note: e.target.value })} /></Field>}
      {!!error && <ErrorNote error={error} />}
      <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!existing && (!key || !v.label)}>{existing ? t("Publish version {{n}}", { n: existing.version + 1 }) : t("Add trait")}</Button></div>
    </form>
  );
}

export function TraitsPage() {
  useLanguage();
  const can = useCan();
  const q = useTraits();
  const [open, setOpen] = useState<Trait | null>(null);
  const [editing, setEditing] = useState<Trait | "new" | null>(null);
  const groups: Array<{ title: string; kinds: TraitKind[] }> = [
    { title: t("Scores and measurements"), kinds: ["score", "measure", "category"] },
    { title: t("EPDs"), kinds: ["epd"] },
    { title: t("Indexes"), kinds: ["index"] },
  ];
  return (
    <GeneticsShell title={t("Traits")} subtitle={t("What each score and number means on this ranch.")} actions={can("manage") && <Button variant="primary" icon="plus" onClick={() => setEditing("new")}>{t("Add a trait")}</Button>}>
      {q.isLoading ? <Loading /> : !q.data ? <ErrorNote error={q.error} /> : groups.map((g) => {
        const items = q.data.traits.filter((x) => g.kinds.includes(x.kind));
        if (!items.length) return null;
        return (
          <Section key={g.title} title={g.title} count={items.length}>
            <List>
              {items.map((x) => (
                <Row key={x.key} icon={x.kind === "epd" || x.kind === "index" ? "report" : "ruler"} tone="genetics" onClick={() => setOpen(x)}
                  title={<>{traitName(x.key, q.data.traits)} <span className="muted mono">{x.key}</span></>}
                  meta={[scaleText(x), x.direction ? t(TRAIT_DIRECTION_LABELS[x.direction]) : null].filter(Boolean).join(" · ")}
                  trailing={x.custom ? <Chip tone="genetics">{t("Ranch")}</Chip> : x.versions > 1 ? <Chip tone="neutral">{t("v{{n}}", { n: x.version })}</Chip> : undefined} />
              ))}
            </List>
          </Section>
        );
      })}
      <Sheet open={!!open && !editing} onClose={() => setOpen(null)} title={open ? traitName(open.key, q.data?.traits) : ""}>{open && <Detail trait={open} onEdit={() => setEditing(open)} />}</Sheet>
      <Sheet open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? t("Add a trait") : editing ? t("New version of {{trait}}", { trait: traitName(editing.key, q.data?.traits) }) : ""}>
        {editing && <TraitForm existing={editing === "new" ? undefined : editing} onDone={() => { setEditing(null); setOpen(null); }} />}
      </Sheet>
    </GeneticsShell>
  );
}
